const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL || "http://localhost:3001";

type TokenGetter = () => Promise<string | null>;
let getSessionToken: TokenGetter | null = null;

/** Registered by AuthSyncProvider so every request carries the Clerk session token. */
export function setSessionTokenGetter(getter: TokenGetter | null) {
  getSessionToken = getter;
}

export class ApiError extends Error {
  status: number;
  errorMessage: string;
  moderation: unknown;

  constructor(status: number, message: string, moderation?: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.errorMessage = message;
    this.moderation = moderation;
  }
}

async function request<T>(
  endpoint: string,
  options: RequestInit = {},
): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`;

  const headers: Record<string, string> = {
    ...(options.headers as Record<string, string>),
  };

  if (options.body && !(options.body instanceof FormData)) {
    headers["Content-Type"] = "application/json";
  }

  const token = await getSessionToken?.().catch(() => null);
  if (token && !headers.Authorization) headers.Authorization = `Bearer ${token}`;

  const response = await fetch(url, { ...options, headers, credentials: "include" });
  const data = await response.json();

  if (!response.ok || data.success === false) {
    throw new ApiError(
      response.status,
      data.error || `Request failed: ${response.statusText}`,
      data.moderation,
    );
  }

  return data as T;
}

export const api = {
  get: <T>(endpoint: string) => request<T>(endpoint, { method: "GET" }),

  post: <T>(endpoint: string, data?: unknown) =>
    request<T>(endpoint, {
      method: "POST",
      body: data ? JSON.stringify(data) : undefined,
    }),

  put: <T>(endpoint: string, data: unknown) =>
    request<T>(endpoint, {
      method: "PUT",
      body: JSON.stringify(data),
    }),

  delete: <T>(endpoint: string) => request<T>(endpoint, { method: "DELETE" }),

  upload: <T>(endpoint: string, formData: FormData) =>
    request<T>(endpoint, {
      method: "POST",
      body: formData,
    }),
};

export function buildQueryString(
  params: Record<string, string | number | boolean | undefined>,
): string {
  const searchParams = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined) {
      searchParams.append(key, String(value));
    }
  }
  const qs = searchParams.toString();
  return qs ? `?${qs}` : "";
}
